import './GtdTooltip.scss';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-date-picker';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import moment from 'moment-timezone';
import { TZ } from '../../../../constants/constants';

const toPickerDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const isExpiryInPast = (date, hhmm) => {
  if (!date || !hhmm) return false;
  const dateStr = moment(date).format('M/D/YYYY');
  const expiry = moment.tz(`${dateStr} ${hhmm}`, 'M/D/YYYY HH:mm', TZ);
  // Same clock-minute as now is allowed (HH:mm has no seconds).
  return expiry.isValid() && expiry.isBefore(moment.tz(TZ), 'minute');
};

const splitStoredTime = (hhmm) => {
  const parsed = moment(hhmm, ['HH:mm', 'H:mm'], true);
  if (!parsed.isValid()) {
    return { hour: '12', minute: '00', period: 'AM' };
  }
  return {
    hour: parsed.format('hh'),
    minute: parsed.format('mm'),
    period: parsed.hour() >= 12 ? 'PM' : 'AM',
  };
};

const toStoredHhmm = (hour, minute, period) => {
  const h = String(hour ?? '').padStart(2, '0');
  const m = String(minute ?? '').padStart(2, '0');
  if (!/^(0[1-9]|1[0-2])$/.test(h)) return null;
  if (!/^[0-5]\d$/.test(m)) return null;
  const parsed = moment(`${h}:${m} ${period}`, 'hh:mm A', true);
  return parsed.isValid() ? parsed.format('HH:mm') : null;
};

const onlyDigits = (value, maxLen) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, maxLen);

export const GtdTooltip = ({
  setActiveBtn,
  aciveOrderType,
  selectedDate,
  selectedTime,
  onUpdateDateTime,
  disabled = false,
}) => {
  const split = splitStoredTime(selectedTime);
  const [hour, setHour] = useState(split.hour);
  const [minute, setMinute] = useState(split.minute);
  const [period, setPeriod] = useState(split.period);

  useEffect(() => {
    const next = splitStoredTime(selectedTime);
    setHour(next.hour);
    setMinute(next.minute);
    setPeriod(next.period);
  }, [selectedTime]);

  const emitIfComplete = (nextHour, nextMinute, nextPeriod) => {
    const stored = toStoredHhmm(nextHour, nextMinute, nextPeriod);
    if (!stored) return;
    onUpdateDateTime({ isTime: true, time: stored });
  };

  const handleHourChange = (e) => {
    const digits = onlyDigits(e.target.value, 2);
    setHour(digits);
    if (digits.length < 2) return;
    const asNum = Number(digits);
    if (asNum < 1 || asNum > 12) return;
    emitIfComplete(digits, minute.padStart(2, '0'), period);
  };

  const handleHourBlur = () => {
    let next = onlyDigits(hour, 2);
    if (!next) next = '12';
    const asNum = Number(next);
    if (asNum < 1 || asNum > 12) next = '12';
    next = String(asNum).padStart(2, '0');
    setHour(next);
    emitIfComplete(next, minute.padStart(2, '0'), period);
  };

  const handleMinuteChange = (e) => {
    const digits = onlyDigits(e.target.value, 2);
    setMinute(digits);
    if (digits.length < 2) return;
    const asNum = Number(digits);
    if (asNum > 59) return;
    emitIfComplete(hour.padStart(2, '0'), digits.padStart(2, '0'), period);
  };

  const handleMinuteBlur = () => {
    let next = onlyDigits(minute, 2);
    if (!next) next = '00';
    const asNum = Number(next);
    if (asNum > 59) next = '00';
    next = String(asNum).padStart(2, '0');
    setMinute(next);
    emitIfComplete(hour.padStart(2, '0'), next, period);
  };

  const handlePeriodToggle = (nextPeriod) => {
    if (nextPeriod === period) return;
    setPeriod(nextPeriod);
    emitIfComplete(
      hour.padStart(2, '0'),
      minute.padStart(2, '0'),
      nextPeriod,
    );
  };

  const draftHhmm = useMemo(
    () => toStoredHhmm(hour, minute, period) || selectedTime,
    [hour, minute, period, selectedTime],
  );
  const expiryInPast = isExpiryInPast(selectedDate, draftHhmm);

  const handlePopoverToggle = (nextShow) => {
    if (nextShow) return;
    const hhmm = toStoredHhmm(hour, minute, period) || selectedTime;
    if (isExpiryInPast(selectedDate, hhmm)) {
      onUpdateDateTime({ resetToNow: true });
    }
  };

  const popover = (
    <Popover className="DayBuysell DayBuysell--gtdTip nondraggable_card">
      <Popover.Body>
        <div className="gtdTip">
          <p className="gtdTip__title">Good ’Til Date</p>
          <div className="gtdTip__field customInput">
            <label className="form-label" htmlFor="gtd-date">
              Date
            </label>
            <DatePicker
              id="gtd-date"
              onChange={(e) => onUpdateDateTime({ isTime: false, date: e })}
              format="MM/dd/yyyy"
              value={toPickerDate(selectedDate)}
              minDate={moment.tz(TZ).toDate()}
              clearIcon={null}
              calendarClassName="gtdCalendar"
              className="gtdDatePicker"
            />
          </div>
          <div className={`gtdTip__field customInput${expiryInPast ? ' is-invalid' : ''}`}>
            <span className="form-label" id="gtd-time-label">
              Time
            </span>
            <div className="gtdTip__timeRow" aria-labelledby="gtd-time-label">
              <div className={`gtdTip__clock${expiryInPast ? ' is-invalid' : ''}`}>
                <input
                  id="gtd-hour"
                  className="gtdTip__clockInput"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  placeholder="hh"
                  aria-label="Hour"
                  aria-invalid={expiryInPast}
                  value={hour}
                  onChange={handleHourChange}
                  onBlur={handleHourBlur}
                />
                <span className="gtdTip__clockSep" aria-hidden="true">
                  :
                </span>
                <input
                  id="gtd-minute"
                  className="gtdTip__clockInput"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  placeholder="mm"
                  aria-label="Minute"
                  aria-invalid={expiryInPast}
                  value={minute}
                  onChange={handleMinuteChange}
                  onBlur={handleMinuteBlur}
                />
              </div>
              <div className="gtdTip__ampm" role="group" aria-label="AM or PM">
                <button
                  type="button"
                  className={`gtdTip__ampmBtn${period === 'AM' ? ' is-active' : ''}`}
                  onClick={() => handlePeriodToggle('AM')}
                >
                  AM
                </button>
                <button
                  type="button"
                  className={`gtdTip__ampmBtn${period === 'PM' ? ' is-active' : ''}`}
                  onClick={() => handlePeriodToggle('PM')}
                >
                  PM
                </button>
              </div>
            </div>
            {expiryInPast && (
              <p className="gtdTip__error" role="alert">
                Expiry time must be after the current time.
              </p>
            )}
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="top"
      overlay={popover}
      rootClose
      onToggle={handlePopoverToggle}
      popperConfig={{
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            options: { padding: 8, boundary: 'viewport' },
          },
        ],
      }}
    >
      <Button
        type="button"
        onClick={() => !disabled && setActiveBtn('GTD')}
        className={`gtcBtn ${aciveOrderType === 'GTD' ? 'active' : ''}`}
        disabled={disabled}
      >
        GTD
      </Button>
    </OverlayTrigger>
  );
};
